<template>
  <div class="risk-q">
    <div class="risk-q__header">
      <h2 class="client-page__title" style="font-size: 1.4rem">Vos réponses déterminent votre profil</h2>
      <!-- <p class="client-page__intro">
        17 questions pour évaluer votre horizon de placement, votre connaissance des marchés et votre tolérance
        au risque. Vos réponses restent modifiables : le résultat se met à jour automatiquement.
      </p> -->
    </div>

    <div class="risk-q__progress" role="status" aria-live="polite">
      <p class="risk-q__progress-page">Page {{ page }} / {{ totalPages }}</p>
      <div class="risk-q__progress-bar">
        <div class="risk-q__progress-fill" :style="{ width: progressPct + '%' }" />
      </div>
      <span class="risk-q__progress-text">{{ answeredCount }} / {{ questions.length }} questions répondues</span>
    </div>

    <section class="client-card risk-q__section">
      <div class="risk-q__questions">
        <fieldset
          v-for="q in currentPageQuestions"
          :key="q.id"
          class="risk-q__question"
          :disabled="submitting"
        >
          <legend class="risk-q__question-text">{{ q.text }}</legend>

          <!-- q07 : grille support x (connaissance, déjà investi). -->
          <div v-if="q.type === 'matrix'" class="risk-q__matrix">
            <div class="risk-q__matrix-row risk-q__matrix-row--head">
              <span class="risk-q__matrix-cell risk-q__matrix-cell--label" />
              <span
                v-for="sq in q.subQuestions"
                :key="sq.id"
                class="risk-q__matrix-cell risk-q__matrix-cell--head"
              >
                {{ sq.label }}
              </span>
            </div>
            <div v-for="inst in q.instruments" :key="inst.id" class="risk-q__matrix-row">
              <span class="risk-q__matrix-cell risk-q__matrix-cell--label">{{ inst.text }}</span>
              <div v-for="sq in q.subQuestions" :key="sq.id" class="risk-q__matrix-cell">
                <label
                  v-for="(opt, idx) in sq.options"
                  :key="idx"
                  class="risk-q__option risk-q__option--compact"
                  :class="{ 'risk-q__option--active': matrixAnswers[inst.id][sq.id] === idx }"
                >
                  <input
                    v-model="matrixAnswers[inst.id][sq.id]"
                    type="radio"
                    :name="`${q.id}_${inst.id}_${sq.id}`"
                    :value="idx"
                    class="risk-q__radio"
                  />
                  <span>{{ opt.label }}</span>
                </label>
              </div>
            </div>
          </div>

          <div v-else class="risk-q__options">
            <label
              v-for="(opt, idx) in q.options"
              :key="idx"
              class="risk-q__option"
              :class="{ 'risk-q__option--active': answers[q.id] === idx }"
            >
              <input
                v-model="answers[q.id]"
                type="radio"
                :name="q.id"
                :value="idx"
                class="risk-q__radio"
              />
              <span>{{ opt.label }}</span>
            </label>
          </div>
        </fieldset>
      </div>
    </section>

    <p v-if="isLastPage && !allAnswered" class="client-alert risk-q__pending">
      Répondez à toutes les questions pour obtenir votre profil de risque.
    </p>

    <div class="risk-q__actions">
      <button
        type="button"
        class="client-btn client-btn--ghost"
        :disabled="page === 1 || submitting"
        @click="onBack"
      >
        Retour
      </button>
      <p-button
        v-if="!isLastPage"
        type="primary"
        :disabled="!currentPageComplete || submitting"
        @click="onNext"
      >
        Continuer
      </p-button>
      <p-button v-else type="primary" :disabled="!allAnswered || submitting" @click="onSubmit">
        {{ submitting ? 'Enregistrement…' : 'Valider mon profil de risque' }}
      </p-button>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref } from 'vue';
import PButton from '@/components/shared/Button.vue';
import { sections, CATEGORIES } from '@/utils/profilInvest';

const emit = defineEmits(['submit']);
const props = defineProps({
  submitting: { type: Boolean, default: false },
});

const questions = sections.flatMap((s) => s.questions);
const matrixQuestion = questions.find((q) => q.type === 'matrix'); // q07

/**
 * Segmentation façon Form.vue/Diagnostic.vue : pages de 4 questions, sauf q07
 * (grille par instrument) isolée sur sa propre page vu sa taille.
 */
function buildPages(qs) {
  const pages = [];
  let current = [];
  for (const q of qs) {
    if (q.type === 'matrix') {
      if (current.length) pages.push(current);
      pages.push([q]);
      current = [];
      continue;
    }
    current.push(q);
    if (current.length === 4) {
      pages.push(current);
      current = [];
    }
  }
  if (current.length) pages.push(current);
  return pages;
}
const pages = buildPages(questions);
const totalPages = pages.length;
const page = ref(1);
const currentPageQuestions = computed(() => pages[page.value - 1] || []);
const isLastPage = computed(() => page.value === totalPages);
/**
 * Classement (table CATEGORIES, 20-72) = somme brute des questions 1 à 16
 * (q07 = somme de ses 10 sous-réponses). Q17 est collectée mais volontairement
 * exclue de ce total : son rôle dans le barème pondéré (5%/20%) reste à définir.
 */
const classificationQuestions = questions.filter(
  (q) => q.type !== 'matrix' && q.id !== 'q17_arbitrage'
);
const q17 = questions.find((q) => q.id === 'q17_arbitrage');

/** Réponse = index de l'option choisie dans `q.options` (les scores peuvent se répéter). */
const answers = reactive(
  Object.fromEntries([...classificationQuestions, q17].map((q) => [q.id, null]))
);

const matrixAnswers = reactive(
  Object.fromEntries(
    matrixQuestion.instruments.map((inst) => [
      inst.id,
      Object.fromEntries(matrixQuestion.subQuestions.map((sq) => [sq.id, null])),
    ])
  )
);
const matrixComplete = computed(() =>
  matrixQuestion.instruments.every((inst) =>
    matrixQuestion.subQuestions.every((sq) => matrixAnswers[inst.id][sq.id] !== null)
  )
);
const matrixScore = computed(() =>
  matrixQuestion.instruments.reduce((sum, inst) => {
    return (
      sum +
      matrixQuestion.subQuestions.reduce((s2, sq) => {
        const idx = matrixAnswers[inst.id][sq.id];
        return s2 + (idx != null ? sq.options[idx].score : 0);
      }, 0)
    );
  }, 0)
);

const answeredCount = computed(
  () =>
    Object.values(answers).filter((v) => v !== null).length + (matrixComplete.value ? 1 : 0)
);
const allAnswered = computed(() => answeredCount.value === questions.length);
const progressPct = computed(() => Math.round((answeredCount.value / questions.length) * 100));

const currentPageComplete = computed(() =>
  currentPageQuestions.value.every((q) =>
    q.type === 'matrix' ? matrixComplete.value : answers[q.id] !== null
  )
);

function goToPage(n) {
  page.value = n;
}
function onBack() {
  if (page.value > 1) goToPage(page.value - 1);
}
function onNext() {
  if (page.value < totalPages && currentPageComplete.value) goToPage(page.value + 1);
}

const totalScore = computed(() =>
  classificationQuestions.reduce((sum, q) => {
    const idx = answers[q.id];
    return sum + (idx != null ? q.options[idx].score : 0);
  }, 0) + matrixScore.value
);

const scorePct = computed(() => {
  const min = CATEGORIES[0].min;
  const max = CATEGORIES[CATEGORIES.length - 1].max;
  const pct = Math.round(((totalScore.value - min) / (max - min)) * 100);
  return Math.min(100, Math.max(0, pct));
});

const category = computed(
  () =>
    CATEGORIES.find((c) => totalScore.value >= c.min && totalScore.value <= c.max) ||
    (totalScore.value < CATEGORIES[0].min ? CATEGORIES[0] : CATEGORIES[CATEGORIES.length - 1])
);

/**
 * CATEGORIES ne porte plus `riskPreference` par palier : dérivé du label.
 * Regroupement par défaut, à confirmer : Conservateur/Prudent -> security,
 * Équilibré -> balanced, Croissant/Dynamique -> performance.
 */
const riskPreference = computed(() => {
  const label = category.value.label;
  if (label.startsWith('Conservateur') || label.startsWith('Prudent')) return 'security';
  if (label.startsWith('Équilibré')) return 'balanced';
  return 'performance';
});

const horizonBand = computed(() => {
  const q = questions.find((q) => q.id === 'q02_horizon');
  const idx = answers.q02_horizon;
  return idx != null ? q.options[idx]?.horizonBand || null : null;
});

function onSubmit() {
  if (!allAnswered.value || props.submitting) return;
  emit('submit', {
    answers: { ...answers },
    matrixAnswers: JSON.parse(JSON.stringify(matrixAnswers)),
    score: totalScore.value,
    scorePct: scorePct.value,
    category: category.value.label,
    riskPreference: riskPreference.value,
    horizonBand: horizonBand.value,
    // Réponse brute de Q17, non intégrée au score : rôle du barème pondéré (5%/20%) à définir.
    q17Answer: answers[q17.id],
  });
}
</script>

<style scoped>
.risk-q {
  max-width: 780px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.risk-q__header {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.risk-q__progress {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.risk-q__progress-page {
  margin: 0;
  font-family: var(--font-body);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--brand-navy);
}

.risk-q__progress-bar {
  height: 6px;
  border-radius: 999px;
  background: rgba(1, 38, 62, 0.08);
  overflow: hidden;
}

.risk-q__progress-fill {
  height: 100%;
  background: var(--brand-gold);
  transition: width 0.25s ease;
}

.risk-q__progress-text {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--client-text-muted, #64748b);
  white-space: nowrap;
}

.risk-q__questions {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.risk-q__question {
  border: none;
  margin: 0;
  padding: 0;
}

.risk-q__question-text {
  padding: 0;
  font-family: var(--font-body);
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--brand-navy);
  margin-bottom: var(--space-2);
}

.risk-q__options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-2);
}

.risk-q__option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-field);
  border: 1px solid rgba(1, 38, 62, 0.1);
  background: #fff;
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--brand-navy);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.risk-q__option:hover {
  border-color: rgba(201, 162, 39, 0.5);
}

.risk-q__option--active {
  border-color: var(--brand-gold);
  background: var(--brand-gold-soft);
}

.risk-q__radio {
  accent-color: var(--brand-gold);
}

.risk-q__matrix {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  overflow-x: auto;
}

.risk-q__matrix-row {
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(2, minmax(220px, 1fr));
  gap: var(--space-3);
  align-items: start;
}

.risk-q__matrix-row--head {
  align-items: end;
}

.risk-q__matrix-cell--label {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--brand-navy);
  padding-top: var(--space-2);
}

.risk-q__matrix-cell--head {
  font-family: var(--font-body);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--client-text-muted, #64748b);
}

.risk-q__matrix-cell {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.risk-q__option--compact {
  padding: 0.35rem 0.6rem;
  font-size: 0.78rem;
}

.risk-q__pending {
  justify-content: center;
  text-align: center;
}

.risk-q__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  justify-content: space-between;
}
</style>
